import React from 'react';
import { BarChart3, Bell, BookOpen, Calendar, LayoutDashboard, LogOut, Phone, Settings,  Users } from 'lucide-react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '../contexts/AppContext';
import { queryClient } from '../lib/query';
import { showToast } from '../lib/utils/toast';
import { useGetMe, useLogout } from '../services/query/auth/auth';
import ConfirmModal from './shared/ConfirmModal';

export default function Navigation() {
  const { state } = useAppContext();
  const { activeCall } = state;
  const navigate = useNavigate();
  const location = useLocation();

  const { data: me } = useGetMe();
  const { mutateAsync: logout, isPending: isLoggingOut } = useLogout();

  const [isProfileOpen, setIsProfileOpen] = React.useState(false);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = React.useState(false);
  const profileMenuRef = React.useRef<HTMLDivElement | null>(null);

  const navItems = [
    { to: '/dashboard', label: 'Overview', icon: LayoutDashboard, end: true },
    { to: '/patients', label: 'Patients', icon: Users },
    { to: '/calls', label: 'Contact Centre', icon: Phone },
    { to: '/appointments', label: 'Appointments', icon: Calendar },
    { to: '/education', label: 'Education', icon: BookOpen },
    { to: '/analytics', label: 'Analytics', icon: BarChart3 },
    { to: '/reminders', label: 'Reminders', icon: Bell },
    // { to: '/settings', label: 'Settings', icon: Settings }
  ];

  const mobileValue = React.useMemo(() => {
    const path = location.pathname;
    if (path.startsWith('/patients')) return '/patients';
    if (path.startsWith('/calls')) return '/calls';
    if (path.startsWith('/appointments')) return '/appointments';
    if (path.startsWith('/education')) return '/education';
    if (path.startsWith('/analytics')) return '/analytics';
    if (path.startsWith('/reminders')) return '/reminders';
    if (path.startsWith('/settings')) return '/settings';
    return '/dashboard';
  }, [location.pathname]);

  const displayName = React.useMemo(() => {
    if (!me) return 'User';
    const fullName = `${me.first_name || ''} ${me.last_name || ''}`.trim();
    return fullName || me.username || me.email || 'User';
  }, [me]);

  const initials = React.useMemo(() => {
    const parts = displayName.split(' ').filter(Boolean);
    if (parts.length === 0) return 'U';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }, [displayName]);

  React.useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!isProfileOpen) return;
      const target = event.target as Node | null;
      if (!target) return;
      if (profileMenuRef.current && !profileMenuRef.current.contains(target)) {
        setIsProfileOpen(false);
      }
    };

    window.addEventListener('pointerdown', handlePointerDown);
    return () => window.removeEventListener('pointerdown', handlePointerDown);
  }, [isProfileOpen]);

  React.useEffect(() => {
    // Close the menu on route change.
    setIsProfileOpen(false);
  }, [location.pathname]);

  const onLogout = React.useCallback(async () => {
    setIsProfileOpen(false);
    setIsLogoutConfirmOpen(false);

    try {
      window.localStorage.setItem('signingOut', 'true');
    } catch {
      // ignore
    }

    try {
      await logout();
    } catch {
      // ignore network/logout failures; still clear local session
    } finally {
      try {
        window.localStorage.removeItem('access_token');
      } catch {
        // ignore
      }

      showToast.success('Logged out successfully');
      queryClient.invalidateQueries({ queryKey: ['me'] });
      navigate('/login', { replace: true });

      // Remove after navigation to avoid unauthorized toasts during signout.
      setTimeout(() => {
        try {
          window.localStorage.removeItem('signingOut');
        } catch {
          // ignore
        }
      }, 0);
    }
  }, [logout, navigate]);

  return (
    <nav className="bg-white shadow-lg">
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-700 rounded-lg flex items-center justify-center">
              <Phone className="w-6 h-6 text-white" />
            </div>
            <div className="text-left">
              <h1 className="text-xl font-bold text-gray-900">Patient Support Wizard</h1>
            </div>
          </button>

          {/* Navigation Items */}
          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                    isActive ? 'bg-green-100 text-green-700' : 'text-gray-600 hover:bg-gray-100'
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
                {item.to === '/calls' && activeCall && (
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                )}
              </NavLink>
            ))}
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden">
            <select
              value={mobileValue}
              onChange={(e) => navigate(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            >
              {navItems.map((item) => (
                <option key={item.to} value={item.to}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-3">
            {/* Status Indicator */}
            {activeCall && (
              <div className="hidden sm:flex items-center space-x-2 px-3 py-2 bg-green-100 rounded-lg">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-green-800">Call Active</span>
              </div>
            )}

            {/* User Profile */}
            <div className="relative" ref={profileMenuRef}>
              <button
                type="button"
                onClick={() => setIsProfileOpen((v) => !v)}
                className="flex items-center space-x-2 px-2 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-haspopup="menu"
                aria-expanded={isProfileOpen}
              >
                <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center border border-gray-200">
                  <span className="text-sm font-semibold text-gray-700">{initials}</span>
                </div>
                {/* <div className="hidden md:block text-left">
                  <div className="text-sm font-normal text-gray-900 leading-4">{displayName}</div>
                  <div className="text-xs text-gray-500">Profile</div>
                </div> */}
              </button>

              {isProfileOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
                >
                  <p className='px-4 text-left text-sm my-2'>{displayName}</p>
                  <Link 
                  role="menuitem" 
                  to={'/settings'}
                  className="w-full flex items-center space-x-2 px-4 py-3 text-sm 
                  text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Settings</span>
                    </Link>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setIsProfileOpen(false);
                      setIsLogoutConfirmOpen(true);
                    }}
                    disabled={isLoggingOut}
                    className="w-full flex items-center space-x-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>{isLoggingOut ? 'Logging out…' : 'Logout'}</span>
                  </button>
                </div>
              )}
            </div>

            <ConfirmModal
              open={isLogoutConfirmOpen}
              onClose={() => setIsLogoutConfirmOpen(false)}
              onConfirm={onLogout}
              title="Log out"
              description="Are you sure you want to log out?"
              confirmText="Logout"
              cancelText="Cancel"
              confirmVariant="danger"
              loading={isLoggingOut}
            />
          </div>
        </div>
      </div>
    </nav>
  );
}